/** @jsxImportSource @emotion/react */
import { css } from '@emotion/react';
import { Box, MenuItem, Select, Switch } from '@mui/material';
import ExpandMoreRoundedIcon from '@mui/icons-material/ExpandMoreRounded';
import dayjs from 'dayjs';
import { FC, memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useAtom } from 'jotai';
import { QSTypography } from '../../atoms';
import LockOutlinedIcon from '../../../icons/icons-revamp/lock_outlined';
import InfoCircleIcon from '../../../icons/icons-revamp/info-circle';
import Loader from '../../molecules/loader';
import QSTooltip from '../../molecules/tooltip';
import useNotifications from '../../../hooks/use-notifications';
import { getSupportAccessStatus, grantSupportAccess, revokeSupportAccess } from '../../../requests/support-access';
import { SupportAccessDuration } from '../../../typings/support-access';
import { supportAccessStatusAtom } from '../../../states/support-access';

const DURATION_OPTIONS: SupportAccessDuration[] = [3, 7, 14];
const DEFAULT_DURATION: SupportAccessDuration = 7;
const SUPPORT_ACCESS_TOOLTIP =
  'When you allow DoubleTick to access your account information we will be able to provide a better response if you come across a unique situation. We will only access your account when you reach out to the Support Team, if necessary.';

const SupportAccessSection: FC = () => {
  const { addNotification } = useNotifications();
  const [status, setStatus] = useAtom(supportAccessStatusAtom);
  const [selectedDuration, setSelectedDuration] = useState<SupportAccessDuration>(
    status?.enabled ? status.durationDays : DEFAULT_DURATION
  );
  const [initialLoading, setInitialLoading] = useState(status === null);
  const [actionLoading, setActionLoading] = useState(false);
  const mutationCountRef = useRef(0);

  useEffect(() => {
    const startCount = mutationCountRef.current;
    const fetchStatus = async () => {
      try {
        const data = await getSupportAccessStatus();
        if (mutationCountRef.current !== startCount) return;
        setStatus(data);
        if (data.enabled) {
          setSelectedDuration(data.durationDays);
        }
      } catch (error) {
        addNotification({
          message: 'Failed to fetch support access status',
          type: 'error',
        });
      } finally {
        setInitialLoading(false);
      }
    };
    fetchStatus();
  }, []);

  const handleGrant = useCallback(
    async (durationDays: SupportAccessDuration) => {
      mutationCountRef.current += 1;
      setActionLoading(true);
      try {
        const data = await grantSupportAccess(durationDays);
        setStatus(data);
        if (data.enabled) {
          setSelectedDuration(data.durationDays);
        }
        addNotification({
          message: 'Support access granted',
          type: 'success',
        });
      } catch (error) {
        addNotification({
          message: 'Failed to grant support access',
          type: 'error',
        });
      } finally {
        setActionLoading(false);
      }
    },
    [addNotification]
  );

  const handleRevoke = useCallback(async () => {
    mutationCountRef.current += 1;
    setActionLoading(true);
    try {
      await revokeSupportAccess();
      setStatus({ enabled: false });
      addNotification({
        message: 'Support access revoked',
        type: 'success',
      });
    } catch (error) {
      addNotification({
        message: 'Failed to revoke support access',
        type: 'error',
      });
    } finally {
      setActionLoading(false);
    }
  }, [addNotification]);

  const isEnabled = status?.enabled === true;

  const handleToggle = useCallback(() => {
    if (actionLoading || initialLoading) return;
    if (isEnabled) {
      handleRevoke();
    } else {
      handleGrant(selectedDuration);
    }
  }, [actionLoading, initialLoading, isEnabled, handleGrant, handleRevoke, selectedDuration]);

  const handleDurationChange = useCallback(
    (durationDays: SupportAccessDuration) => {
      setSelectedDuration(durationDays);
      if (isEnabled && durationDays !== (status?.enabled ? status.durationDays : null)) {
        handleGrant(durationDays);
      }
    },
    [handleGrant, isEnabled, status]
  );

  const expiryLabel = useMemo(() => {
    if (status?.enabled && status.expiresAt) {
      return `Expires on ${dayjs(status.expiresAt).format('MMM D YYYY')}`;
    }
    return null;
  }, [status]);

  return (
    <Box
      css={css`
        border-bottom: 1px solid #e0e1e4;
        padding: 12px 16px;
        display: flex;
        gap: 8px;
      `}
    >
      <Box
        css={css`
          flex-shrink: 0;
          padding-top: 2px;
        `}
      >
        <LockOutlinedIcon size={20} color="#7A7A7A" />
      </Box>
      <Box
        css={css`
          flex: 1;
          min-width: 0;
          display: flex;
          flex-direction: column;
          gap: 8px;
        `}
      >
        <Box
          css={css`
            display: flex;
            align-items: flex-start;
            justify-content: space-between;
            gap: 8px;
          `}
        >
          <Box
            css={css`
              display: flex;
              flex-direction: column;
              gap: 2px;
              min-width: 0;
            `}
          >
            <Box
              css={css`
                display: flex;
                align-items: center;
                gap: 6px;
              `}
            >
              <QSTypography
                css={css`
                  font-size: 14px;
                  font-weight: 500;
                  color: #2d2d2d;
                `}
              >
                Support access
              </QSTypography>
              <QSTooltip title={SUPPORT_ACCESS_TOOLTIP} placement="top" arrow>
                <Box
                  css={css`
                    display: inline-flex;
                  `}
                >
                  <InfoCircleIcon size={14} color="#7A7A7A" />
                </Box>
              </QSTooltip>
            </Box>
            <QSTypography
              css={css`
                font-size: 12px;
                font-weight: 400;
                color: #7a7a7a;
              `}
            >
              Allow DoubleTick to access your account
            </QSTypography>
          </Box>
          <Box
            css={css`
              display: flex;
              align-items: center;
              gap: 6px;
              flex-shrink: 0;
            `}
          >
            {(initialLoading || actionLoading) && <Loader size={16} />}
            <Switch
              checked={isEnabled}
              onChange={handleToggle}
              disabled={initialLoading || actionLoading}
              sx={{
                '& .MuiSwitch-switchBase': {
                  '&.Mui-checked': {
                    color: '#4d9e82',
                    '& + .MuiSwitch-track': {
                      backgroundColor: '#4d9e8250',
                      opacity: 1,
                    },
                  },
                },
                '& .MuiSwitch-track': {
                  backgroundColor: '#7a7a7a',
                  opacity: 1,
                },
              }}
            />
          </Box>
        </Box>

        {isEnabled && (
          <Box
            css={css`
              display: flex;
              flex-direction: column;
              gap: 4px;
            `}
          >
            <Select
              value={selectedDuration}
              onChange={(event) => handleDurationChange(Number(event.target.value) as SupportAccessDuration)}
              disabled={actionLoading || initialLoading}
              IconComponent={ExpandMoreRoundedIcon}
              size="small"
              sx={{
                width: '100%',
                fontSize: '14px',
                fontWeight: 500,
                color: '#2d2d2d',
                borderRadius: '4px',
                '& .MuiOutlinedInput-notchedOutline': {
                  borderColor: '#e0e1e4',
                },
                '&:hover .MuiOutlinedInput-notchedOutline': {
                  borderColor: '#c0c4ca',
                },
                '& .MuiSelect-select': {
                  padding: '8px 12px',
                },
              }}
            >
              {DURATION_OPTIONS.map((option) => (
                <MenuItem key={option} value={option} sx={{ fontSize: '14px' }}>
                  For {option} days
                </MenuItem>
              ))}
            </Select>
            {expiryLabel && (
              <QSTypography
                css={css`
                  font-size: 12px;
                  font-weight: 400;
                  color: #7a7a7a;
                `}
              >
                {expiryLabel}
              </QSTypography>
            )}
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default memo(SupportAccessSection);
